// src/abode.ts
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { getCurrentScript } from "tiny-current-script";
var componentSelector = "data-component";
var components = {};
var unPopulatedElements = [];
var register = (name, fn, options) => {
  components[name] = { module: retry(fn, 10, 20), options };
};
var delay = (ms) => new Promise((res) => setTimeout(res, ms));
var retry = async (fn, times, delayTime) => {
  try {
    return await fn();
  } catch (err) {
    if (times > 1) {
      await delay(delayTime);
      return retry(fn, times - 1, delayTime * 2);
    } else {
      throw new Error(err);
    }
  }
};
var setComponentSelector = (selector) => {
  componentSelector = selector;
};
var getRegisteredComponents = () => {
  return components;
};
var getActiveComponents = () => {
  return Array.from(
    new Set(getAbodeElements().map((el) => el.getAttribute(componentSelector)))
  );
};
var getCleanPropName = (raw) => {
  return raw.replace("data-prop-", "").replace(/-./g, (x) => x.toUpperCase()[1]);
};
var getElementProps = (el, options) => {
  var _a, _b, _c, _d;
  const props = {};
  if (el == null ? void 0 : el.attributes) {
    const rawProps = Array.from(el.attributes).filter(
      (attribute) => attribute.name.startsWith("data-prop-")
    );
    for (const prop of rawProps) {
      const componentName = getComponentName(el) ?? "";
      const propName = getCleanPropName(prop.name);
      const propParser = ((_a = options == null ? void 0 : options.propParsers) == null ? void 0 : _a[propName]) ?? ((_d = (_c = (_b = components[componentName]) == null ? void 0 : _b.options) == null ? void 0 : _c.propParsers) == null ? void 0 : _d[propName]);
      if (propParser) {
        props[propName] = propParser(prop.value);
      } else {
        if (/^0+\d+$/.test(prop.value)) {
          props[propName] = prop.value;
        } else {
          try {
            props[propName] = JSON.parse(prop.value);
          } catch (e) {
            props[propName] = prop.value;
          }
        }
      }
    }
  }
  return props;
};
var getScriptProps = (options) => {
  const element = getCurrentScript();
  if (element === null) {
    throw new Error("Failed to get current script");
  }
  return getElementProps(element, options);
};
var getAbodeElements = () => {
  return Array.from(document.querySelectorAll(`[${componentSelector}]`)).filter(
    (el) => {
      const component = el.getAttribute(componentSelector);
      return component && components[component];
    }
  );
};
var setUnpopulatedElements = () => {
  unPopulatedElements = getAbodeElements().filter(
    (el) => !el.getAttribute("react-abode-populated")
  );
};
var setAttributes = (el, attributes) => {
  for (const [k, v] of Object.entries(attributes)) {
    el.setAttribute(k, v);
  }
};
function getComponentName(el) {
  var _a;
  return (_a = Array.from(el.attributes).find((at) => at.name === componentSelector)) == null ? void 0 : _a.value;
}
var renderAbode = async (el, root) => {
  var _a;
  const props = getElementProps(el);
  const componentName = getComponentName(el);
  if (!componentName || componentName === "") {
    throw new Error(
      `not all react-abode elements have a value for  ${componentSelector}`
    );
  }
  const module = await ((_a = components[componentName]) == null ? void 0 : _a.module);
  if (!module) {
    throw new Error(`no component registered for ${componentName}`);
  }
  const element = module.default || module;
  root.render(createElement(element, props));
};
var trackPropChanges = (el, root) => {
  if (MutationObserver) {
    const observer = new MutationObserver(() => {
      renderAbode(el, root);
    });
    observer.observe(el, { attributes: true });
  }
};
function unmountOnNodeRemoval(element, root) {
  const observer = new MutationObserver(() => {
    function isDetached(el) {
      if (el.parentNode === document) {
        return false;
      } else if (el.parentNode === null) {
        return true;
      } else {
        return isDetached(el.parentNode);
      }
    }
    if (isDetached(element)) {
      observer.disconnect();
      root.unmount();
    }
  });
  observer.observe(document, {
    childList: true,
    subtree: true
  });
}
var update = async (elements, options) => {
  for (const el of elements) {
    el.setAttribute("react-abode-populated", "true");
  }
  for (const el of elements) {
    const root = createRoot(el);
    if (options == null ? void 0 : options.attributes)
      setAttributes(el, options.attributes);
    renderAbode(el, root);
    trackPropChanges(el, root);
    unmountOnNodeRemoval(el, root);
  }
};
var checkForAndHandleNewComponents = async (options) => {
  setUnpopulatedElements();
  if (unPopulatedElements.length) {
    await update(unPopulatedElements, options);
    unPopulatedElements = [];
    if (options == null ? void 0 : options.callback)
      options.callback();
  }
};
var populate = async (options) => {
  await checkForAndHandleNewComponents(options);
  const observer = new MutationObserver(async (mutationList) => {
    for (const mutation of mutationList) {
      if (mutation.type === "childList") {
        await checkForAndHandleNewComponents(options);
      }
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};
export {
  getActiveComponents,
  getRegisteredComponents,
  getScriptProps,
  populate,
  register,
  setComponentSelector
};
//# sourceMappingURL=index.mjs.map